import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "edit-note-header" }
const _hoisted_2 = { class: "modal-body-content" }
const _hoisted_3 = { class: "form-group" }

import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useStore } from "vuex";
import { Modal, Button, RichTextEditor, Icon } from "@/components/ui";
import ActionConfirmationModal from "@/components/modal/ActionConfirmationModal.vue";
import { useToast } from "@/composables/ui/useToast";
import { useFormDraft } from "@/composables/shared";
import { useEditNote, useUploadNoteAttachment } from "@/composables/notes";

// Props
interface Props {
  selectedNote: {
    id: string;
    message: string;
    contactId: string;
    [key: string]: any;
  };
}


export default /*@__PURE__*/_defineComponent({
  __name: 'EditNoteModal',
  props: {
    selectedNote: {}
  },
  emits: ["closeEditNoteModal", "refreshNotes"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

// Emits
const emit = __emit;

// Store (only for account)
const store = useStore();
const account = computed(() => store.getters.account.data);

// Edit note mutation
const editNoteMutation = useEditNote();
const isLoading = computed(() => editNoteMutation.isPending.value);

const uploadToNote = useUploadNoteAttachment();
const pendingUploads = ref(0);
const showCloseConfirm = ref(false);
const toast = useToast();
let beforeUnloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;

const handleEditorUpload = async (payload: {
  file: File;
  kind: "image" | "video" | "file";
  placeholderId?: string;
  signal?: AbortSignal;
  onProgress?: (pct: number) => void;
  onSuccess?: (url: string) => void;
  onError?: (msg?: string) => void;
}) => {
  if (!props.selectedNote?.id) {
    payload.onError?.("Missing note id");
    return;
  }
  try {
    const result = await uploadToNote.mutateAsync({
      noteId: props.selectedNote.id,
      files: [payload.file],
      onProgress: payload.onProgress,
      signal: payload.signal,
    });
    const uploaded = result?.[0];
    if (uploaded?.url && payload.onSuccess) {
      payload.onSuccess(uploaded.url);
    } else {
      payload.onError?.("Upload failed");
    }
  } catch (err: any) {
    if (err?.code === "ERR_CANCELED" || err?.name === "AbortError") return;
    payload.onError?.(err?.message || "Upload failed");
  }
};

watch(pendingUploads, (n) => {
  if (n > 0 && !beforeUnloadHandler) {
    beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "Uploads in progress. Leave anyway?";
    };
    window.addEventListener("beforeunload", beforeUnloadHandler);
  } else if (n === 0 && beforeUnloadHandler) {
    window.removeEventListener("beforeunload", beforeUnloadHandler);
    beforeUnloadHandler = null;
  }
});

// State
const showModal = ref(true);
const editNoteObj = ref({
  id: null as string | null,
  message: "" as string,
  contactId: null as string | null,
});

const notesToolbar = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ color: [] }],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link", "image", "attach-file"],
  ["clean"],
];

// Template ref
const editEditor = ref<any>(null);

const { loadDraft: loadEditNoteDraft, clearDraft: clearEditNoteDraft } = useFormDraft(editNoteObj, {
  key: `edit-note:${props.selectedNote?.id}`,
  fields: ["message"],
  isEmpty: (v) => !v.message?.trim(),
});

// Methods
const setValues = () => {
  if (props.selectedNote) {
    editNoteObj.value.id = props.selectedNote.id;
    editNoteObj.value.message = props.selectedNote.message || "";
    editNoteObj.value.contactId = props.selectedNote.contactId;
  }
  const draft = loadEditNoteDraft();
  if (draft?.message?.trim()) {
    editNoteObj.value.message = draft.message;
  }
};

const closeModal = () => {
  if (editEditor.value?.hasPendingUploads?.()) {
    showCloseConfirm.value = true;
    return;
  }
  emit("closeEditNoteModal");
};

const confirmCloseAndDiscard = () => {
  showCloseConfirm.value = false;
  editEditor.value?.cancelAllUploads?.();
  emit("closeEditNoteModal");
};

const editNote = async () => {
  if (!editNoteObj.value.message?.trim()) return;
  if (pendingUploads.value > 0) {
    toast.warning("Please wait for uploads to finish.");
    return;
  }
  const id = editNoteObj.value.id ?? "";
  const contactId = editNoteObj.value.contactId ?? "";
  if (!id || !contactId) return;

  if (editEditor.value?.stripAllPlaceholders) {
    editEditor.value.stripAllPlaceholders();
    const fresh = editEditor.value?.getHTML?.();
    if (typeof fresh === "string") editNoteObj.value.message = fresh;
  }

  try {
    const result = await editNoteMutation.mutateAsync({
      id,
      message: editNoteObj.value.message,
      contactId,
      organizationId: account.value?.organizationId ?? "",
    });

    if (result) {
      clearEditNoteDraft();
      emit("refreshNotes");
      emit("closeEditNoteModal");
    }
  } catch (error) {
    console.error("Error editing note:", error);
  }
};

onMounted(() => {
  setValues();
});

onBeforeUnmount(() => {
  editEditor.value?.cancelAllUploads?.();
  if (beforeUnloadHandler) {
    window.removeEventListener("beforeunload", beforeUnloadHandler);
    beforeUnloadHandler = null;
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(Modal), {
      show: showModal.value,
      size: "md",
      "dialog-max-width": "550px",
      onClose: closeModal,
      headerVariant: "blue",
      "blur-backdrop": false,
      backdropOpacity: 0.3,
      zIndex: 10070,
      "show-close-button": false
    }, {
      header: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _cache[3] || (_cache[3] = _createElementVNode("div", { class: "edit-note-header__text" }, [
            _createElementVNode("h2", { class: "edit-note-header__title" }, "Edit Note"),
            _createElementVNode("p", { class: "edit-note-header__subtitle" }, "Update note content")
          ], -1)),
          _createElementVNode("button", {
            type: "button",
            class: "edit-note-header__close",
            onClick: closeModal,
            "aria-label": "Close modal"
          }, [
            _createVNode(_unref(Icon), { icon: "mdi:close" })
          ])
        ])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _cache[4] || (_cache[4] = _createElementVNode("label", { class: "form-label" }, "Note", -1)),
            _createVNode(_unref(RichTextEditor), {
              ref_key: "editEditor",
              ref: editEditor,
              modelValue: editNoteObj.value.message,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((editNoteObj.value.message) = $event)),
              height: "180px",
              toolbar: notesToolbar,
              "accepts-uploads": true,
              onUpload: handleEditorUpload,
              onUploadsPending: _cache[1] || (_cache[1] = ($event: any) => (pendingUploads.value = $event))
            }, null, 8, ["modelValue"])
          ])
        ])
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(Button), {
          variant: "secondary",
          onClick: closeModal
        }, {
          default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
            _createTextVNode(" Cancel ", -1)
          ]))]),
          _: 1
        }),
        _createVNode(_unref(Button), {
          variant: "primary",
          loading: isLoading.value,
          disabled: !editNoteObj.value.message?.trim() || pendingUploads.value > 0,
          onClick: editNote
        }, {
          default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
            _createTextVNode(" Save ", -1)
          ]))]),
          _: 1
        }, 8, ["loading", "disabled"])
      ]),
      _: 1
    }, 8, ["show"]),
    _createVNode(ActionConfirmationModal, {
      show: showCloseConfirm.value,
      title: "Discard upload?",
      message: "Uploads are still in progress. Closing now will cancel them.",
      "confirm-text": "Discard",
      "cancel-text": "Keep open",
      onClose: _cache[2] || (_cache[2] = ($event: any) => (showCloseConfirm.value = false)),
      onConfirm: confirmCloseAndDiscard
    }, null, 8, ["show"])
  ], 64))
}
}

})